import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Icon } from '../../../components/Component';

const CardReorderModal = ({ show, onHide, section, cards, onReorder }) => {
  const [reorderedCards, setReorderedCards] = useState([]);

  useEffect(() => {
    if (cards && cards.length > 0) {
      // Sort cards by their current order or index
      const sortedCards = [...cards].sort((a, b) => (a.order || 0) - (b.order || 0));
      setReorderedCards(sortedCards);
    }
  }, [cards, show]);

  const handleDragEnd = (result) => {
    console.log('Drag end result:', result);

    if (!result.destination) {
      return; // Return early if no valid destination
    }

    const { source, destination } = result;

    if (source.index === destination.index) {
      return; // Return early if there's no actual change in the ordering
    }

    // Reorder cards based on the drag and drop operation
    const updatedCards = Array.from(reorderedCards);
    const [removed] = updatedCards.splice(source.index, 1);
    updatedCards.splice(destination.index, 0, removed);

    setReorderedCards(updatedCards);
  };

  const handleSave = () => {
    // Create the payload with new order
    const cardsWithNewOrder = reorderedCards.map((card, index) => ({
      id: card.id,
      order: index
    }));

    onReorder(section.id, cardsWithNewOrder);
    onHide();
  };

  const handleClose = () => {
    // Reset to original order
    if (cards && cards.length > 0) {
      const sortedCards = [...cards].sort((a, b) => (a.order || 0) - (b.order || 0));
      setReorderedCards(sortedCards);
    }
    onHide();
  };

  if (!reorderedCards || reorderedCards.length === 0) {
    return (
      <Modal isOpen={show} toggle={handleClose} size="lg">
        <ModalHeader toggle={handleClose}>Reorder Cards - {section?.name}</ModalHeader>
        <ModalBody>
          <div className="text-center py-4">
            <p className="text-muted">No cards to reorder.</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={show} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Reorder Cards - {section.name}</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <p className="text-muted">
            Drag and drop the cards below to reorder them. The first card will appear at the top left of the section.
          </p>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="modal-card-reorder-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0px',
                  minHeight: '200px'
                }}
              >
                {reorderedCards.map((card, index) => (
                  <Draggable key={`modal-card-${card.id}`} draggableId={`modal-card-${card.id}`} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          userSelect: 'none',
                          margin: '0 0 12px 0',
                          padding: '1rem',
                          backgroundColor: snapshot.isDragging ? '#f8f9fa' : '#fff',
                          border: '1px solid #e3e7ed',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                          boxShadow: snapshot.isDragging ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
                          ...provided.draggableProps.style // <- always last
                        }}
                      >
                        <div className="me-3">
                          <Icon name="move" style={{ color: '#9aa0ac' }} />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-semibold">{card.title}</h6>
                          {card.link && (
                            <small className="text-muted">
                              <Icon name="external" className="me-1" style={{ fontSize: '0.7rem' }} />
                              {card.link.startsWith('http') ? 'External Link' : 'Internal Link'}
                            </small>
                          )}
                        </div>
                        <div className="text-muted">
                          <small>#{index + 1}</small>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave}>
          Save Order
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CardReorderModal;
